<script>
  import { createEventDispatcher } from "svelte";
  import { daSourceOptions, loadDaSourceManifest } from "./stores.js";
  import { onMount } from 'svelte';

  export let initialData = null;
  const dispatch = createEventDispatcher();

  // Make formState reactive to initialData changes
  $: formState = {
    name: initialData?.name || "",
    startDate: initialData?.startDate || "",
    endDate: initialData?.endDate || "",
    basicPay: initialData?.basicPay || null,
    salaryType: initialData?.salaryType || "employee",
    daSource: initialData?.daSource || "manual",
    commutation: {
      opted: initialData?.commutation?.opted || false,
      amount: initialData?.commutation?.amount || null,
      startDate: initialData?.commutation?.startDate || "",
      endDate: initialData?.commutation?.endDate || "",
    },
  };

  $: isEditing = !!initialData?.id;

  onMount(() => {
    loadDaSourceManifest();
  });

  function setEndDateToToday() {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    formState.endDate = `${year}-${month}`;
  }

  function handleCalculate() {
    dispatch("calculate", formState);
  }

  function handleSave() {
    dispatch("save", formState);
  }
</script>

<div class="form-container glass-card">
  <form on:submit|preventDefault={handleCalculate}>
    <div class="form-group">
      <!-- FIX: Use `for` attribute to link label to input -->
      <label for="calc-name">Calculation Name</label>
      <input
        id="calc-name"
        type="text"
        placeholder="e.g., My Arrears 2023"
        required
        bind:value={formState.name}
      />
    </div>

    <div class="form-group">
      <label for="start-date">Period</label>
      <div class="date-group">
        <input
          id="start-date"
          type="month"
          required
          bind:value={formState.startDate}
          title="Start Date"
        />
        <span style="margin: 0 5px;">to</span>
        <input
          type="month"
          required
          bind:value={formState.endDate}
          title="End Date"
          aria-label="End Date"
        />
      </div>
      <button
        type="button"
        class="btn btn-secondary btn-small"
        style="width: auto; margin-top: 5px;"
        on:click={setEndDateToToday}>Set End to Today</button
      >
    </div>

    <div class="form-group">
      <label for="basic-pay">Basic Pay / Pension (₹)</label>
      <input
        id="basic-pay"
        type="number"
        step="0.01"
        required
        bind:value={formState.basicPay}
      />
    </div>

    <div class="form-group">
      <label for="salary-type">Salary Type</label>
      <div class="select-wrapper">
        <select id="salary-type" bind:value={formState.salaryType}>
          <option value="employee">Regular Employee</option>
          <option value="pensioner">Pensioner</option>
        </select>
      </div>
    </div>

    {#if formState.salaryType === "pensioner"}
      <div class="form-group pensioner-options">
        <div class="option-card">
          <div class="option-header">
            <div class="checkbox-wrapper">
              <input
                type="checkbox"
                id="commutationOpted"
                bind:checked={formState.commutation.opted}
              />
              <label for="commutationOpted" class="checkbox-label">
                <span class="checkbox-custom"></span>
                <span class="checkbox-text">Opted for Commuted Pension</span>
              </label>
            </div>
            <div class="option-info">
              <span class="info-icon">ℹ️</span>
              <span class="info-text">Commuted pension reduces monthly pension for a fixed period</span>
            </div>
          </div>

          {#if formState.commutation.opted}
            <div class="commutation-details">
              <div class="commutation-grid">
                <div class="form-group">
                  <label for="commutation-amount">Commuted Amount (₹)</label>
                  <input
                    id="commutation-amount"
                    type="number"
                    placeholder="e.g., 5000"
                    step="0.01"
                    bind:value={formState.commutation.amount}
                  />
                  <small class="field-help">Monthly amount received as commuted pension</small>
                </div>
                <div class="form-group">
                  <label for="commutation-start">Commutation Period</label>
                  <div class="date-group">
                    <input
                      id="commutation-start"
                      type="month"
                      title="Commutation Start Date"
                      bind:value={formState.commutation.startDate}
                    />
                    <span class="date-separator">to</span>
                    <input
                      type="month"
                      title="Commutation End Date"
                      aria-label="Commutation End Date"
                      bind:value={formState.commutation.endDate}
                    />
                  </div>
                  <small class="field-help">Period during which commuted amount is received</small>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <div class="form-group">
      <label for="da-source">DA/DR Data Source</label>
      <div class="select-wrapper">
        <select id="da-source" bind:value={formState.daSource}>
          {#each $daSourceOptions as group}
            {#if group.options}
              <optgroup label={group.group}>
                {#each group.options as option}
                  <option value={option.value}>{option.name}</option>
                {/each}
              </optgroup>
            {:else}
              <option value={group.value}>{group.name}</option>
            {/if}
          {/each}
        </select>
      </div>
    </div>

    <div class="action-buttons">
      <button type="submit" class="btn btn-primary"
        >Calculate / Update Table</button
      >
      <button
        type="button"
        class="btn btn-secondary"
        on:click={handleSave}
        disabled={!formState.name}
      >
        {isEditing ? "Update Calculation" : "Save Calculation"}
      </button>
    </div>
  </form>
</div>

<style>
  .pensioner-options {
    margin-top: 20px;
  }

  .option-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--glass-border);
    border-radius: 12px;
    padding: 20px;
    transition: all 0.3s ease;
  }

  .option-card:hover {
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.08);
  }

  .option-header {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 15px;
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-weight: 500;
    color: var(--text-color);
  }

  .checkbox-custom {
    width: 18px;
    height: 18px;
    border: 2px solid var(--glass-border);
    border-radius: 4px;
    position: relative;
    transition: all 0.2s ease;
  }

  input[type="checkbox"]:checked + .checkbox-label .checkbox-custom {
    background: var(--primary-color);
    border-color: var(--primary-color);
  }

  input[type="checkbox"]:checked + .checkbox-label .checkbox-custom::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 12px;
    font-weight: bold;
  }

  .checkbox-text {
    font-size: 1rem;
  }

  .option-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    padding: 8px 12px;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    border-left: 3px solid #3b82f6;
  }

  .info-icon {
    font-size: 0.9rem;
  }

  .info-text {
    font-size: 0.85rem;
    color: var(--text-muted);
    line-height: 1.4;
  }

  .commutation-details {
    border-top: 1px solid var(--glass-border);
    padding-top: 20px;
  }

  .commutation-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .field-help {
    display: block;
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-top: 4px;
    line-height: 1.3;
  }

  .date-separator {
    margin: 0 8px;
    color: var(--text-muted);
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .option-header {
      flex-direction: column;
      gap: 10px;
    }

    .commutation-grid {
      grid-template-columns: 1fr;
      gap: 15px;
    }

    .option-info {
      margin-left: 28px;
    }
  }
</style>
